import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined, ScissorOutlined } from "@ant-design/icons";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getAllStock,
  getInventoryHistory,
  markInventoryConsumed,
} from "../../api/inventoryApi";
import { searchOrderItems } from "../../api/orderApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const formatSource = (value) =>
  ({
    SYSTEM_PROCESS: "Auto Process",
    MANUAL_USER: "User Marked",
    USER_SETTLEMENT: "Settlement",
    USER_RECEIPT: "Receipt",
  }[value] || value || "-");

const flattenStockOptions = (groups = []) =>
  groups.flatMap((group) =>
    (group.batches || []).map((batch) => ({
      id: batch.id,
      label: `${group.material?.name || "Material"}${group.reel_width ? ` • RS ${group.reel_width}"` : ""}${batch.batch_no ? ` • Batch ${batch.batch_no}` : ""}`,
      material_code: group.material?.code,
      available_qty: batch.available_qty,
      unit: group.material?.unit,
      reel_width: batch.reel_width,
      batch_no: batch.batch_no,
    }))
  );

const InventoryConsumptionHistory = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");
  const role = localStorage.getItem("role");
  const isAdmin = role === "admin";

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [stockGroups, setStockGroups] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);
  const watchedStockId = Form.useWatch("stock_id", form);

  const stockOptions = useMemo(() => flattenStockOptions(stockGroups), [stockGroups]);
  const selectedStock = stockOptions.find((item) => item.id === watchedStockId);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [historyResp, stockResp] = await Promise.all([
        getInventoryHistory({
          from_date: dateRange[0].format("YYYY-MM-DD"),
          to_date: dateRange[1].format("YYYY-MM-DD"),
        }),
        getAllStock(),
      ]);

      setHistory(historyResp?.data || []);
      setStockGroups(stockResp || []);
    } catch (error) {
      toast.error("Failed to load inventory history");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }

  checkTokenExp(accessToken);

  const handleSearchOrders = async (search) => {
    if (!search || search.trim().length < 2) {
      setOrderOptions([]);
      return;
    }

    try {
      const response = await searchOrderItems({ search, limit: 10 });
      setOrderOptions(response?.data || []);
    } catch (error) {
      setOrderOptions([]);
    }
  };

  const handleManualConsume = async (values) => {
    try {
      setSubmitting(true);
      const selectedOrderItem = orderOptions.find((item) => item.id === values.order_item_id);

      await markInventoryConsumed({
        stock_id: values.stock_id,
        quantity: values.quantity,
        order_id: selectedOrderItem?.order?.id,
        order_number: selectedOrderItem?.order?.order_number,
        order_item_id: selectedOrderItem?.id,
        order_item_name: selectedOrderItem?.item_name,
        reason: values.reason,
        notes: values.notes,
      });

      toast.success("Inventory marked as consumed");
      setOpen(false);
      form.resetFields();
      setOrderOptions([]);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to mark inventory as consumed");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (value) => moment(value).format("DD MMM YYYY, hh:mm A"),
    },
    {
      title: "Inventory",
      key: "inventory",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.material?.name || "-"}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.material?.code || "-"}
            {row.reel_width ? ` • RS ${row.reel_width}"` : ""}
            {row.batch_no ? ` • Batch ${row.batch_no}` : ""}
          </Text>
        </Space>
      ),
    },
    {
      title: "Event",
      key: "event",
      width: 120,
      render: (_, row) => (
        <Tag color={row.movement_type === "RECEIPT" ? "green" : row.movement_type === "SETTLEMENT" ? "gold" : "blue"}>
          {row.movement_type}
        </Tag>
      ),
    },
    {
      title: "Source",
      dataIndex: "event_source",
      key: "event_source",
      width: 130,
      render: (value) => <Tag>{formatSource(value)}</Tag>,
    },
    {
      title: "Qty",
      key: "quantity",
      width: 110,
      render: (_, row) => (
        <Text strong type={row.quantity < 0 ? "danger" : "success"}>
          {row.quantity}
        </Text>
      ),
    },
    {
      title: "Linked Order / Job",
      key: "reference",
      render: (_, row) => row.order_number || row.job_number || "-",
    },
    {
      title: "Marked By",
      dataIndex: "created_by_name",
      key: "created_by_name",
      width: 150,
      render: (value) => value || "System",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
    },
  ];

  return (
    <>
      <PageTitle title="Back" subtitle="Inventory Consumption History" />

      <Card
        extra={
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates)}
              allowClear={false}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchData}>
              Refresh
            </Button>
            {isAdmin && (
              <Button type="primary" icon={<ScissorOutlined />} onClick={() => setOpen(true)}>
                Mark Consumed
              </Button>
            )}
          </Space>
        }
      >
        <Table
          loading={loading}
          columns={columns}
          dataSource={history}
          rowKey="id"
          scroll={{ x: 1100 }}
        />
      </Card>

      <Modal
        title="Mark Inventory as Consumed"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText="Save"
      >
        <Form form={form} layout="vertical" onFinish={handleManualConsume}>
          <Form.Item
            label="Inventory Batch / Reel"
            name="stock_id"
            rules={[{ required: true, message: "Select inventory" }]}
          >
            <Select
              showSearch
              placeholder="Select stock entry"
              optionFilterProp="children"
            >
              {stockOptions.map((option) => (
                <Option key={option.id} value={option.id}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Consumed Quantity"
            name="quantity"
            rules={[{ required: true, message: "Enter consumed quantity" }]}
          >
            <InputNumber min={0.001} style={{ width: "100%" }} />
          </Form.Item>

          {selectedStock && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Available: {selectedStock.available_qty} {selectedStock.unit}
              </Text>
            </Card>
          )}

          <Form.Item label="Map to Order Item (Optional)" name="order_item_id">
            <Select
              showSearch
              allowClear
              placeholder="Search order / item"
              filterOption={false}
              onSearch={handleSearchOrders}
              notFoundContent="Type at least 2 characters"
            >
              {orderOptions.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.order?.order_number} - {item.item_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Reason" name="reason">
            <Input placeholder="Why is this being marked as consumed?" />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default InventoryConsumptionHistory;
